import type { GhGroup } from "@kwp/contracts";
import { cx } from "../../ui";
import { GH_GROUP_VIEWS, type GhItemFilter, type SourceView } from "./sources";
import type { GitHubSummary } from "./use-github";

/** A change of source view: GitHub, a group, and one filter inside it. */
export type OnSource = (view: SourceView) => void;

const ITEM_LABELS: Record<GhItemFilter, string> = { pr: "Pull requests", issue: "Issues" };

/** One filter inside a group, as a select value: everything, PRs, issues, or one repository. */
function filterValue(view: SourceView): string {
  if (view.repo !== undefined) return `repo:${view.repo}`;
  if (view.item !== undefined) return `item:${view.item}`;
  return "";
}

/**
 * Over GitHub's own list: the groups with their unread counts, and inside a group one filter (PRs,
 * issues or a repository; one at a time, as with quick filters, since each has its own index).
 */
export function GitHubHeader({
  view,
  summary,
  repos,
  onSource,
}: {
  view: SourceView;
  summary: GitHubSummary | null;
  /** The repositories to offer: those of the conversations listed, and the one chosen. */
  repos: readonly string[];
  onSource: OnSource;
}) {
  const group = view.gh;
  const choose = (gh: GhGroup | undefined) => onSource({ app: view.app, gh, item: undefined, repo: undefined });
  return (
    <div className="flex flex-col gap-2 border-b border-border px-3 py-2 text-sm">
      <h2 className="font-semibold text-text">GitHub</h2>
      <div role="group" aria-label="GitHub groups" className="flex flex-wrap gap-1">
        <button type="button" aria-pressed={group === undefined} className={tabClass(group === undefined)} onClick={() => choose(undefined)}>
          All
        </button>
        {GH_GROUP_VIEWS.map((g) => {
          const n = summary === null ? null : summary.groups[g.key];
          return (
            <button
              key={g.key}
              type="button"
              aria-pressed={group === g.key}
              aria-label={`${g.label}${n === null ? "" : `, ${n} unread`}`}
              className={tabClass(group === g.key)}
              onClick={() => choose(g.key)}
            >
              {g.label}
              {n !== null && n > 0 ? <span className="ml-1 rounded-full bg-accent px-1.5 text-xs font-semibold text-accent-contrast">{n}</span> : null}
            </button>
          );
        })}
      </div>
      {group === undefined ? null : (
        <label className="flex items-center gap-2 text-xs text-muted">
          Show
          <select
            aria-label="Filter GitHub"
            value={filterValue(view)}
            onChange={(e) => {
              const v = e.target.value;
              if (v.startsWith("repo:")) onSource({ app: view.app, gh: group, item: undefined, repo: v.slice(5) });
              else if (v.startsWith("item:")) onSource({ app: view.app, gh: group, item: v.slice(5) as GhItemFilter, repo: undefined });
              else onSource({ app: view.app, gh: group, item: undefined, repo: undefined });
            }}
            className="rounded border border-border bg-surface px-1 py-0.5 text-text"
          >
            <option value="">Everything</option>
            {(Object.keys(ITEM_LABELS) as GhItemFilter[]).map((item) => (
              <option key={item} value={`item:${item}`}>
                {ITEM_LABELS[item]}
              </option>
            ))}
            {repos.map((repo) => (
              <option key={repo} value={`repo:${repo}`}>
                {repo}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}

function tabClass(on: boolean): string {
  return cx("rounded-full border px-2 py-0.5 text-xs", on ? "border-accent bg-accent text-accent-contrast" : "border-border text-text hover:bg-surface-2");
}
